// The ONE renderer the #2270 / #6124 account-plane verbs share — the operator,
// control-plane and account kinds that were filed as `mcp_tool.call.*` with their name
// in `tool` until each got its own `AuditEventKind`.
//
// Shared rather than per kind, because the payloads of ~130 verbs from ~50 writers
// agree on nothing but the verb itself, and what they rendered as BEFORE was exactly
// that: "MCP tool <name> finished" off `row.tool`. The line now reads the name off
// `row.kind` (a first-class row has no `tool`), so the console line is unchanged in
// substance and the row is findable on the indexed column. A kind that later earns a
// richer line moves to its own renderer in its family file.
//
// The four tables below are each spread into ITS family's composed group (the family
// the kind's `../../audit/kind-*.ts` neighbours live in), so the partition test sees
// them where the vocabulary does, and this file is the one list of what #2270 and #6124
// promoted.
//
// Three variants. `queued` is a request or a re-queue recorded BEFORE the work runs
// (`plan.execute_requested`, `runner.ssh_bootstrap_queued`, `review.rerun`,
// `ask.redriven`, `ci_job.requeued`, and #6124's `plan.start_*`, `plan.ask_filed`,
// `project.setup_dispatched` and `key_label_audit.redrive`), at `info`. `refused`
// covers the `*_refused` / `*_failed` kinds and the few refusals whose name does not
// say so (`*_unnudged`, the err-only names; `account-verb.test.ts`
// `REFUSED_BY_NAME_EXCEPTION`) at `warn`, not `fail`: they are decisions to say no or
// audited best-effort misses, never a retry ladder, so there is no attempt to carry.
// The subject is the row's session (the account's gateway parent), the same subject
// the `mcp_tool.call.*` renderers give these rows' history.

import type { TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';

function accountVerbRendered(
  ctx: TerminalRenderContext,
  key:
    | 'terminal.account_verb.ok'
    | 'terminal.account_verb.queued'
    | 'terminal.account_verb.refused',
): TerminalRendered {
  const { row } = ctx;
  return {
    line: { key, params: { verb: row.kind } },
    subject: { kind: 'session', id: row.sessionId, label: row.sessionId },
    refs: { sessionId: row.sessionId },
  };
}

/** A verb that did what it was asked. */
export const ACCOUNT_VERB_OK = {
  level: 'ok',
  actor: 'system',
  render: (ctx: TerminalRenderContext) => accountVerbRendered(ctx, 'terminal.account_verb.ok'),
} as const;

/**
 * A verb whose row is written when work is REQUESTED or put back on a queue, before it
 * runs: `info`, and "queued", because "done" would claim an outcome nobody has seen.
 * The row that says the work ran is its own (`plan.executed`, the review's own trail).
 */
export const ACCOUNT_VERB_QUEUED = {
  level: 'info',
  actor: 'system',
  render: (ctx: TerminalRenderContext) => accountVerbRendered(ctx, 'terminal.account_verb.queued'),
} as const;

/** A verb that was refused, or an audited best-effort step that missed. */
export const ACCOUNT_VERB_REFUSED = {
  level: 'warn',
  actor: 'system',
  render: (ctx: TerminalRenderContext) => accountVerbRendered(ctx, 'terminal.account_verb.refused'),
} as const;

/**
 * `task.moved` (#6768), the one account verb whose level is the ROW's (`by_row`). One settled move item, landed or failed, in ONE row shape
 * (`{ task_id, batch_id, from, to, state, failure }`). Its level and message are the
 * settlement's: a landed move is `ok`, "done"; a failed one (`source_ended`,
 * `target_released`, `cancelled`) is `warn`, "did not go through", never a success line for
 * a job that never landed. A row whose `state` does not read `landed` is never shown as done.
 */
export const TASK_MOVED = {
  level: 'by_row',
  actor: 'system',
  render: (ctx: TerminalRenderContext) => {
    const input = ctx.row.input;
    const landed =
      typeof input === 'object' &&
      input !== null &&
      (input as { state?: unknown }).state === 'landed';
    return landed
      ? { ...accountVerbRendered(ctx, 'terminal.account_verb.ok'), level: 'ok' as const }
      : { ...accountVerbRendered(ctx, 'terminal.account_verb.refused'), level: 'warn' as const };
  },
} as const;

const OK = ACCOUNT_VERB_OK;
const QUEUED = ACCOUNT_VERB_QUEUED;
const NO = ACCOUNT_VERB_REFUSED;

/** `../../audit/kind-maintenance.ts`: task, plan, PR, project, onboarding and sign-in verbs. */
export const ACCOUNT_VERBS_MAINTENANCE = {
  'task.cancel_refused': NO,
  'task.redispatch_refused': NO,
  'task.dependencies_added': OK,
  // #6751: the pause doors: a request (the box's own `task.paused` row follows it), its
  // withdrawal, a resume.
  'task.pause_requested': OK,
  'task.pause_withdrawn': OK,
  'task.resumed': OK,
  // #6768: one moved job landed on its target (ok), or its move failed (warn).
  'task.moved': TASK_MOVED,
  'plan.approved': OK,
  'plan.approve_refused': NO,
  'plan.approved_noop': OK,
  'plan.regrouped': OK,
  'plan.regroup_refused': NO,
  'plan.unlink_refused': NO,
  'plan.edges_unlinked': OK,
  'plan.cancelled': OK,
  'pr.babysit_rearmed': OK,
  'project.enabled': OK,
  'project.disabled': OK,
  'pr.decision_undone': OK,
  'project.enable_refused': NO,
  // #6124
  'project.created': OK,
  'project.record_failed': NO,
  'project.scaffold_dispatch_failed': NO,
  'project.scaffold_plan_failed': NO,
  'project.setup_dispatched': QUEUED,
  'project.setup_plan_failed': NO,
  'repo.canary_enabled': OK,
  'repo.canary_disabled': OK,
  'repo.public_stats_enabled': OK,
  'repo.public_stats_disabled': OK,
  'repo.merge_override_set': OK,
  'repo.merge_override_refused': NO,
  'squad.created': OK,
  'squad.renamed': OK,
  'squad.repos_set': OK,
  'squad.deleted': OK,
  'squad.write_refused': NO,
  'repo_binding.user_token_bind': OK,
  'repo_binding.user_token_bind_refused': NO,
  'repo_binding.user_token_unbind': OK,
  'onboarding.advanced': OK,
  'onboarding.intent_persisted': OK,
  'onboarding.intent_persist_failed': NO,
  'auth.login': OK,
  'auth.session_reach_extended': OK,
  'sso.user_linked': OK,
  'sso.link_collision': NO,
  'sso.role_elevated': OK,
  'sso.link_refused_foreign_memberships': NO,
  'sso.user_created': OK,
  'sso.identity_relinked': OK,
  'sso.users_merged': OK,
  'sso.identity_unlinked': OK,
  'identity.link_requested': QUEUED,
  'identity.users_merged': OK,
  'identity.link_refused': NO,
  'org_sso.set': OK,
  'org_sso.removed': OK,
  'org_sso.removed_noop': OK,
  'github_token.connected': OK,
  'github_token.disconnected': OK,
  'account.active_account_switch': OK,
  'account.posture_changed': OK,
  'user.tos_accepted': OK,
  'user.region_set': OK,
  'user.consent_granted': OK,
  'user.consent_withdrawn': OK,
  'user.approved': OK,
  'user.suspended': OK,
  'admin.provision': OK,
  'admin.provision_partial': NO,
} as const;

/** `../../audit/kind-dispatch.ts`: plan dispatch and start, CI and runner verbs. */
export const ACCOUNT_VERBS_DISPATCH = {
  'plan.execute_requested': QUEUED,
  'plan.file_switch_set': OK,
  'plan.model_set': OK,
  'ci_job.requeued': QUEUED,
  'runner.enrollment_created': OK,
  'runner.enroll_refused': NO,
  'runner.ssh_bootstrap_queued': QUEUED,
  'runner.ssh_bootstrap_queue_failed': NO,
  'runner.log_sink_delivery_failed': NO,
  'runner.api_key_mint_failed': NO,
  'runner.org_lookup_failed': NO,
  'runner.template_lookup_failed': NO,
  'runner.queue_retired': OK,
  'runner.queue_retire_failed': NO,
  'runner.revoke_cascade_failed': NO,
  'runner.drained': OK,
  'runner.undrained': OK,
  'runner.cordoned': OK,
  // #6751: a machine's jobs paused or resumed in one press, and an epic paused or resumed.
  'runner.jobs_paused': OK,
  'runner.jobs_resumed': OK,
  'plan.paused': OK,
  'plan.resumed': OK,
  // #6768: a move of one job or a batch between machines was accepted.
  'move.batch': OK,
  'box_grant.offered': OK,
  'box_grant.accepted': OK,
  'box_grant.revoked': OK,
  'box_grant.write_refused': NO,
  'box_grant.reservations_released': OK,
  'runner.owner_set': OK,
  'runner.owner_set_refused': NO,
  'runner.prompt_set': OK,
  'runner.renamed': OK,
  'runner.server_labelled': OK,
  // #6767: a person sent work to one machine; the sweep released a sent task's target.
  'task.sent_to_machine': OK,
  'task.target_released': OK,
  // #6740
  'runner.max_slots_set': OK,
  'runner.max_slots_reset': OK,
  'runner.isolation_attested': OK,
  'runner.isolation_withdrawn': OK,
  'runner.release_unyanked': OK,
  // #6735
  'runner.promote_requested': OK,
  'runner.promote_dispatch_failed': NO,
  // Sent, answer lost: the run may be going, so "queued", never "done" or "refused".
  'runner.promote_dispatch_unknown': QUEUED,
  // #6124
  'plan.file_refused': NO,
  'plan.start_dispatched': QUEUED,
  'plan.start_held': QUEUED,
  'plan.start_context': QUEUED,
  'plan.start_refused': NO,
  'plan.ask_filed': QUEUED,
  'plan.ask_unnudged': NO,
  'ci_log.ref_refused': NO,
  'triage.info_answer_held': OK,
} as const;

/** `../../audit/kind-commerce.ts`: ask, entitlement, member, config, mail and artifact verbs. */
export const ACCOUNT_VERBS_COMMERCE = {
  'ask.redriven': QUEUED,
  'ask.redrive_refused': NO,
  'ask.raw_task_unnudged': NO,
  // The ask named an issue and the platform did NOT link it (#7303): a decision
  // to say no, so it reads as a refusal rather than as a verb that did what it
  // was asked. The name does not say `refused`, so it is listed in
  // `account-verb.test.ts`'s `REFUSED_BY_NAME_EXCEPTION` beside the other names
  // whose row is one.
  'ask.issue_link_skipped': NO,
  'account.set_tier': OK,
  'account.set_seats': OK,
  'account.set_spend_cap': OK,
  'account.oss_verify_decide': OK,
  'org_member.role_changed': OK,
  'template.set': OK,
  'template.deleted': OK,
  'config.set': OK,
  'config.deleted': OK,
  // #6124
  'org_member.invited': OK,
  'org_member.removed': OK,
  'access_request.invited': OK,
  'access_request.declined': OK,
  'access_request.claim_lost': NO,
  'invitation.refused': NO,
  'invitation.minted': OK,
  'invitation.revoked': OK,
  'invitation.accepted': OK,
  'invitation.resent': OK,
  'invitation.fulfil_refused': NO,
  'invitation.already_a_member': OK,
  'blocker.submit': NO,
  'blocker.raw_task_unnudged': NO,
  'key_outage.drain_unnudged': NO,
  'key_label_audit.redrive': QUEUED,
  'secret.saved': OK,
  'secret.deleted': OK,
  'escalation.acked': OK,
  'repo_secret.stored': OK,
  'repo_secret.deleted': OK,
  'webhook.created': OK,
  'webhook.deleted': OK,
  'webhook.tested': OK,
  'webhook.delete_refused': NO,
  'webhook_source.connected': OK,
  'webhook_source.rotated': OK,
  'webhook_source.updated': OK,
  'webhook_source.disconnected': OK,
  'log_sink.set': OK,
  'log_sink.cleared': OK,
  'system_prompt.set': OK,
  'system_prompt.cleared': OK,
  'dashboard.upserted': OK,
  'dashboard.updated': OK,
  'dashboard.deleted': OK,
  'oss_verification.applied': OK,
  'email_route.set': OK,
  'email_route.reset': OK,
  'email_route.reset_noop': OK,
  'notification_pref.set': OK,
  'notification_pref.reset': OK,
  'notification_pref.replaced': OK,
  'notification.test_email': OK,
  'email_status.retry': OK,
  'email.unsubscribed': OK,
  'push.subscribe': NO,
  'artifact.put': OK,
  'artifact.shared': OK,
  'artifact.deleted': OK,
  // A refused delete: the object's DELETE was rejected, so nothing was removed
  // and the row was kept. `NO` (refused) because the name says `_failed`, and
  // because it is exactly that — a step that missed, not a verb that did.
  'artifact.delete_failed': NO,
  'artifact.storage_unusable': NO,
  'artifact.share_notify_failed': NO,
} as const;

/** `../../audit/kind-review.ts`. */
export const ACCOUNT_VERBS_REVIEW = {
  'review.rerun': QUEUED,
  'backlog.import_requested': QUEUED,
  'backlog.import_enqueue_failed': NO,
} as const;
